import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDashboard } from '../../context/DashboardContext';
import { useUserRole } from '../../hooks/useUserRole';
import { useStoreBuckets, useStoreSummary } from '../../hooks/useStoresData';
import DevFilterBar from '../layout/DevFilterBar';
import MonthPicker from '../kuroco/MonthPicker';
import StoreOneView from './StoreOneView';
import StoreAllView from './StoreAllView';
import { trackScopeChange } from '../../analytics/appAnalytics';
import { pickShop } from '../../utils/storeView';
import {
  paramsForRoster,
  paramsForShop,
  paramsForStoresView,
  parseStoresView,
  resolveShop,
  storesViewLabels,
} from '../../utils/storesTab';
import '../../styles/stores.css';

/**
 * 店舗 tab — この店舗 ｜ 全店比較 (admin store view, mock v2).
 * 「この店舗」: ヒーロー・人数ブロック（管理者のみ）・スコアカード・推移（StoreOneView）。
 * 「全店比較」: 見えている店舗の合計 ＋ 店舗カード（達成率の低い順。StoreAllView）。
 * 数字はすべて store-summary（個人名なし）から。
 */
export default function StoresScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { role, canAccessIam } = useUserRole();
  const { selectedMonth, setSelectedMonth, months, formatMonthDisplay, myProfile } = useDashboard();

  const view = parseStoresView(searchParams.get('view'));
  const labels = storesViewLabels(role);
  const summary = useStoreSummary(selectedMonth);
  const buckets = useStoreBuckets(selectedMonth, canAccessIam);
  // 選んだ店舗（URL の shop → ホームの店舗 → 自分の主店舗）が、見える店舗に無ければ先頭の店舗
  const wanted = resolveShop(searchParams) || myProfile?.home_store || null;
  const shop = pickShop(summary.data?.stores, wanted) ?? wanted;

  // 管理者タブのスタイリスト一覧へ（その店舗・同じ月）。stylist は外す（店舗を変える操作と同じ）
  const openRoster = (store) => navigate(`/admin?${paramsForRoster(searchParams, store, selectedMonth)}`);

  // Only `shop` changes — the home's store / stylist stay as they were.
  const changeShop = (next) => setSearchParams((prev) => paramsForShop(prev, next));

  // 全店比較の店舗カードをタップ → その店舗の「この店舗」へ（shop だけを変える。ホームの store / stylist は触らない）
  const openStore = (store) => {
    setSearchParams((prev) => paramsForStoresView(paramsForShop(prev, store), 'one'));
    trackScopeChange('store');
  };

  const changeView = (next) => {
    if (next === view) return;
    setSearchParams((prev) => paramsForStoresView(prev, next));
    trackScopeChange(next === 'all' ? 'company' : 'store');
  };

  return (
    <div className="fade">
      <div className="scope-seg" role="tablist" aria-label="店舗の表示">
        {Object.entries(labels).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            className={view === key ? 'is-on' : undefined}
            onClick={() => changeView(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'one' && <DevFilterBar hideStylist storeValue={shop} onStoreChange={changeShop} />}

      <div className="home-period-row">
        <MonthPicker
          value={selectedMonth}
          onChange={setSelectedMonth}
          availableMonths={months}
          formatMonthDisplay={formatMonthDisplay}
          title="表示月を選択"
          className="home-month-picker"
        />
      </div>

      {view === 'one' ? (
        <StoreOneView
          summary={summary}
          buckets={buckets}
          shop={shop}
          isAdmin={canAccessIam}
          selectedMonth={selectedMonth}
          onOpenRoster={openRoster}
        />
      ) : (
        <StoreAllView
          summary={summary}
          buckets={buckets}
          isAdmin={canAccessIam}
          myStore={myProfile?.home_store || null}
          title={labels.all}
          onPick={openStore}
        />
      )}
    </div>
  );
}
